/**
 * FixGuidancePanel
 * Panel displaying fix guidance links (TSG docs, videos)
 */

import { makeStyles, tokens, Text, Link } from '@fluentui/react-components'
import {
  DocumentTextRegular,
  VideoRegular,
  SparkleRegular,
  BookOpenRegular,
} from '@fluentui/react-icons'
import type { FixGuidanceLink } from '../../../../domain'
import { PanelHeader } from '../shared'
import { usePanelContainerStyles } from '../../styles/usePanelContainerStyles'

const useStyles = makeStyles({
  linkList: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  linkItem: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  linkIcon: {
    color: tokens.colorBrandForeground1,
    fontSize: '14px',
    flexShrink: 0,
  },
  emptyState: {
    color: tokens.colorNeutralForeground3,
    fontStyle: 'italic',
  },
})

const getIconForType = (type: FixGuidanceLink['type']) => {
  switch (type) {
    case 'tsg_fix':
      return <DocumentTextRegular />
    case 'tsg_general':
      return <BookOpenRegular />
    case 'ai_video':
      return <SparkleRegular />
    case 'user_video':
      return <VideoRegular />
    default:
      return <DocumentTextRegular />
  }
}

export interface FixGuidancePanelProps {
  title?: string
  links: FixGuidanceLink[]
}

export function FixGuidancePanel({ title = 'Fix Guidance', links }: FixGuidancePanelProps) {
  const containerStyles = usePanelContainerStyles()
  const styles = useStyles()

  return (
    <div className={containerStyles.root}>
      <PanelHeader icon={<BookOpenRegular />} title={title} />
      {links.length > 0 ? (
        <div className={styles.linkList}>
          {links.map((link) => (
            <div key={link.id} className={styles.linkItem}>
              <span className={styles.linkIcon}>{getIconForType(link.type)}</span>
              <Link href={link.url} target="_blank" rel="noopener noreferrer">
                {link.label}
              </Link>
            </div>
          ))}
        </div>
      ) : (
        <Text size={200} className={styles.emptyState}>
          No guidance available
        </Text>
      )}
    </div>
  )
}
